import { useEffect, useMemo, useState } from "react";
import { CREATIVE_HUB_URL, PROMISES, SECTIONS, FRAMES } from "../data/creative.js";

// The photography SOP and shot menu, as a browsable library rather than a
// document. The frames are the argument: every note says why that specific
// frame works, so the standard is shown instead of described.
//
// This page runs full-bleed and dark rather than inside the usual 640px paper
// shell. A four-column masonry of photographs needs the width, and a reference
// library of stage-lit images reads wrong on white.

const TAG_KEYS = Object.keys(PROMISES);

// The mandate the meter measures: one delivered frame in three shows a real smile.
const SMILE_TARGET = 1 / 3;

function Meter({ frames }) {
  const smiles = frames.filter((f) => f.smile).length;
  const pct = frames.length ? Math.round((smiles / frames.length) * 100) : 0;

  return (
    <div className="cr-meter">
      <span className="cr-mlab">Smile ratio · target 1 in 3</span>
      <span className="cr-track" title="The tick marks the 33% mandate">
        <span className="cr-fill" style={{ width: pct + "%" }} />
        <span className="cr-tick" style={{ left: SMILE_TARGET * 100 + "%" }} />
      </span>
      <span className="cr-pct">{pct}%</span>
      <span className="cr-mlab">
        {frames.length} frame{frames.length === 1 ? "" : "s"}
      </span>
    </div>
  );
}

function Frame({ item, onOpen }) {
  return (
    <button className="cr-card" onClick={onOpen}>
      <img
        src={`/creative/frames/${item.id}.jpg`}
        alt={item.note}
        loading="lazy"
        decoding="async"
      />
      <span className="cr-fid">{item.id}</span>
      {item.smile === 1 && <span className="cr-sm">Smile</span>}
      <span className="cr-cap">{item.note}</span>
    </button>
  );
}

function Lightbox({ frames, index, onClose, onMove }) {
  const item = frames[index];

  // Arrow keys and Esc, because a reference library gets paged through fast.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onMove(1);
      if (e.key === "ArrowLeft") onMove(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onMove]);

  if (!item) return null;
  const section = SECTIONS.find((s) => s.id === item.sec);

  return (
    <div className="cr-lb" role="dialog" aria-modal="true" aria-label="Frame detail">
      <div className="cr-lbstage">
        <div className="cr-lbimg">
          <img src={`/creative/frames/${item.id}.jpg`} alt={item.note} />
        </div>
        <div className="cr-lbinfo">
          <div>
            <div className="cr-fid2">Frame {item.id}</div>
            <h3>{section?.name || item.sec}</h3>
            {section?.kicker && <div className="cr-lbkick">{section.kicker}</div>}
          </div>

          {item.tags.length > 0 && (
            <div className="cr-tagrow">
              {item.tags.map((t) => (
                <span key={t} className="cr-tg">
                  {PROMISES[t]?.name || t}
                </span>
              ))}
              {item.smile === 1 && <span className="cr-tg y">Smile</span>}
            </div>
          )}

          <div>
            <div className="cr-why">Why this frame works</div>
            <p className="cr-note">{item.note}</p>
          </div>
        </div>
      </div>
      <div className="cr-lbbar">
        <span className="cr-count">
          {index + 1} of {frames.length}
        </span>
        <div className="cr-nav">
          <button className="cr-lbbtn" onClick={() => onMove(-1)}>
            ← Prev
          </button>
          <button className="cr-lbbtn" onClick={() => onMove(1)}>
            Next →
          </button>
          <button className="cr-lbbtn" onClick={onClose}>
            Close (Esc)
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Creative({ back }) {
  const [channel, setChannel] = useState("all");
  const [tags, setTags] = useState([]);
  const [smileOnly, setSmileOnly] = useState(false);
  const [open, setOpen] = useState(null);

  const shown = useMemo(
    () =>
      FRAMES.filter((f) => {
        if (channel !== "all" && f.sec !== channel) return false;
        if (smileOnly && f.smile !== 1) return false;
        // Multiple promises read as "any of these", so stacking them widens the
        // set rather than narrowing it to frames that prove all four at once.
        if (tags.length && !tags.some((t) => f.tags.includes(t))) return false;
        return true;
      }),
    [channel, tags, smileOnly],
  );

  const toggleTag = (t) =>
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const reset = () => {
    setChannel("all");
    setTags([]);
    setSmileOnly(false);
  };

  const move = (d) =>
    setOpen((i) => (i === null ? i : (i + d + shown.length) % shown.length));

  const sections = channel === "all" ? SECTIONS : SECTIONS.filter((s) => s.id === channel);
  const filtered = tags.length > 0 || smileOnly;

  return (
    <div className="cr">
      <div className="cr-top">
        <button className="cr-back" onClick={back}>
          ← Back to the hub
        </button>
        <a className="cr-hub" href={CREATIVE_HUB_URL} target="_blank" rel="noreferrer">
          Creative Hub — the archive ↗
        </a>
      </div>

      <header className="cr-head">
        <div className="cr-mark">
          THE CLLXTV <b>/</b> PHOTO
        </div>
        <div className="cr-eyebrow">Training deck · Photography team</div>
        <h1 className="cr-h1">
          Sundays
          <br />
          are a <span className="cr-thin">Festival</span>
        </h1>

        <div className="cr-thesis">
          <p>
            We are not covering a service. We are covering a festival — of joy, of
            smiles, of people finding their tribe.{" "}
            <strong>
              Every frame in here is evidence that God's love is changing somebody's life
              in real time.
            </strong>{" "}
            Eight shot types, {FRAMES.length} reference frames. Tap any photo to read why
            it works. This library itself only clears 22%, and the standing mandate is
            one in three. That gap is the assignment.
          </p>
          <div className="cr-promises">
            {TAG_KEYS.map((k, i) => (
              <div className="cr-promise" key={k}>
                <span className="cr-pn">{String(i + 1).padStart(2, "0")}</span>
                <span className="cr-pt">{PROMISES[k].line}</span>
                <span className="cr-pc">{PROMISES[k].name}</span>
              </div>
            ))}
          </div>
        </div>
      </header>

      <div className="cr-deck">
        <div className="cr-channels">
          <button
            className={"cr-ch" + (channel === "all" ? " on" : "")}
            onClick={() => setChannel("all")}
          >
            <span className="cr-num">CH 00</span>
            <span className="cr-nm">All</span>
            <span className="cr-ct">{FRAMES.length}</span>
          </button>
          {SECTIONS.map((s, i) => (
            <button
              key={s.id}
              className={"cr-ch" + (channel === s.id ? " on" : "")}
              onClick={() => setChannel(s.id)}
            >
              <span className="cr-num">CH {String(i + 1).padStart(2, "0")}</span>
              <span className="cr-nm">{s.name}</span>
              <span className="cr-ct">{FRAMES.filter((f) => f.sec === s.id).length}</span>
            </button>
          ))}
        </div>

        <div className="cr-filters">
          <span className="cr-flabel">Filter</span>
          {TAG_KEYS.map((t) => (
            <button
              key={t}
              className={"cr-chip" + (tags.includes(t) ? " on" : "")}
              onClick={() => toggleTag(t)}
            >
              {PROMISES[t].name}
            </button>
          ))}
          <button
            className={"cr-chip smile" + (smileOnly ? " on" : "")}
            onClick={() => setSmileOnly((v) => !v)}
          >
            Smiles only
          </button>
          {(filtered || channel !== "all") && (
            <button className="cr-chip" onClick={reset}>
              Reset
            </button>
          )}
          <Meter frames={shown} />
        </div>
      </div>

      <main className="cr-main">
        {sections.map((s) => {
          const inSec = shown.filter((f) => f.sec === s.id);
          if (!inSec.length) return null;
          return (
            <section className="cr-sec" key={s.id}>
              <div className="cr-sechead">
                <div>
                  <div className="cr-chnum">
                    CH {String(SECTIONS.findIndex((x) => x.id === s.id) + 1).padStart(2, "0")}
                  </div>
                  <h2>{s.name}</h2>
                  <div className="cr-kick">{s.kicker}</div>
                </div>
                <div>
                  <p className="cr-body">{s.body}</p>
                  <span className="cr-quota">{s.quota}</span>
                </div>
              </div>
              <div className="cr-grid">
                {inSec.map((f) => (
                  <Frame
                    key={f.id}
                    item={f}
                    onOpen={() => setOpen(shown.findIndex((x) => x.id === f.id))}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {shown.length === 0 && (
          <div className="cr-empty">
            No frames match that combination. <button onClick={reset}>Reset the filters</button>
          </div>
        )}
      </main>

      <footer className="cr-foot">
        <div className="cr-footgrid">
          <div>
            <h4>Before you get out of the car</h4>
            <ul className="cr-check">
              <li>Am I shooting the festival, or the service?</li>
              <li>20 frames before the first song</li>
              <li>One in three frames has a real smile</li>
              <li>All four promises covered before I pack up</li>
              <li>35mm on the body. Wide stays in the bag.</li>
              <li>Chest height minimum. Never shoot down at people.</li>
              <li>Have I been in the lobby in the last 20 minutes?</li>
              <li>Am I close enough? (No.)</li>
            </ul>
          </div>
          <div>
            <h4>We don't shoot these</h4>
            <ul className="cr-check never">
              <li>Empty chairs</li>
              <li>The back of the room mid-announcement</li>
              <li>Anyone on their phone unless they're filming the stage</li>
              <li>Half-lit faces in a dead moment</li>
              <li>Twenty frames of the same worship lead</li>
              <li>Anything that makes the room look smaller than it is</li>
              <li>Anything that makes church look like an obligation</li>
            </ul>
            <p className="cr-consent">
              Consent holds above everything: no identifiable minor without a parent's
              okay, no face mid-breakdown published without asking after, and anyone who
              waves you off gets the camera lowered immediately. Shoot it, flag it, ask
              later.
            </p>
          </div>
        </div>
        <div className="cr-signoff">
          <div className="cr-big">
            Friends bring friends
            <br />
            to church.
          </div>
          <div className="cr-sm2">
            Disruptors — never attendees, members, or congregants · History. Happens. Here.
          </div>
        </div>
      </footer>

      {open !== null && (
        <Lightbox
          frames={shown}
          index={open}
          onClose={() => setOpen(null)}
          onMove={move}
        />
      )}
    </div>
  );
}
